import classNames from 'classnames';
import { ComponentChild, h, JSX } from 'preact';

import { DeleteButton } from './DeleteButton';
import { Select, SelectProps } from './Select';
import { CellProps, Column, Table, TableClasses } from './Table';

export interface TicketRow {
    key: string;
    typeInput: SelectProps;
    numbered?: {
        rowNumber: number;
        seatNumber: number;
        sectionName?: string;
    };
    unnumberedText?: string;
    onRemove: () => void;
}

export interface TicketsTableProps {
    headers: {
        tickets: {
            singular: string;
            plural: string;
        };
        section?: string;
        row: string;
        seat: string;
        remove: string;
    };
    rows: TicketRow[];
}

// TODO simpler structure on mobile

export const TicketsTable = (props: TicketsTableProps): JSX.Element => {
    const classes: TableClasses<TicketRow> = {
        table: 'w-full',
        headerCell: 'p-2 py-3 sm:px-4 text-sm text-neutral-500 font-normal',
        headerCellFirst: 'pl-0',
        headerCellLast: 'pr-0',
        bodyCell: 'px-2 sm:px-4 pb-1',
        bodyCellFirst: 'pl-0',
        bodyCellLast: 'pr-0',
    };

    const ticketsText = (
        props.rows.length === 1 ?
            props.headers.tickets.singular :
            props.headers.tickets.plural
    );

    // Determine if we need to show section column
    const hasSection = props.rows.some((row) => row.numbered?.sectionName);
    
    const columns: Column<TicketRow>[] = [
        {
            header: `${props.rows.length} ${ticketsText}`,
            headerClass: 'w-full text-left',
            render: (row): ComponentChild => <Select {...row.typeInput} />,
        },
    ];
    
    // Add section column if any seat has a section
    if (hasSection && props.headers.section) {
        columns.push({
            header: props.headers.section,
            headerClass: 'text-center',
            render: (row): ComponentChild => {
                if (row.numbered) {
                    return row.numbered.sectionName || '-';
                }
                return null;
            },
            getClass: (): string => 'text-center text-nowrap min-w-24',
        });
    }
    
    columns.push(
        {
            header: props.headers.row,
            headerClass: 'text-center',
            render: (row): ComponentChild => {
                if (row.numbered) {
                    return row.numbered.rowNumber;
                }
                return row.unnumberedText;
            },
            getClass: (row): string => classNames(
                'text-center',
                'text-nowrap',
                row.numbered ? 'min-w-16' : 'min-w-32',
            ),
            getProps: (row): CellProps => {
                if (!row.numbered) {
                    // Calculate colspan based on whether section column is shown
                    const colspan = hasSection && props.headers.section ? 3 : 2;
                    return { colSpan: colspan };
                }
                return {};
            },
        },
        {
            header: props.headers.seat,
            headerClass: 'text-center',
            render: (row): ComponentChild => row.numbered?.seatNumber,
            getClass: (): string => 'text-center min-w-16',
        },
        {
            header: props.headers.remove,
            headerClass: 'text-center',
            render: (row): ComponentChild => <DeleteButton onClick={row.onRemove} />,
            getClass: (): string => 'text-center',
        },
    );

    return <Table classes={classes} columns={columns} rows={props.rows} />;
};
